/**
 * ============================================================================
 * MenuManagementPage — 메뉴 관리 (트리 + Drawer 편집)
 * ============================================================================
 *
 * 【역할】
 *   사이드바/탑바를 구성하는 메뉴(fw_menu) 계층 구조를 트리 UI 로 관리.
 *   내부 DataSet 은 평면(flat) 형태로 유지하고 표시 시점에만 parentId 기반
 *   tree 로 변환하여 antd Tree 컴포넌트에 전달한다.
 *
 * 【주요 기능】
 *   - 검색: 자동 조회 (페이지 진입 시 전체 메뉴 1회 로드)
 *   - CRUD: 루트 메뉴 추가 / 하위 메뉴 추가 / 삭제 / 벌크 저장
 *   - 편집: 트리 노드 선택 → '편집' 버튼 → 우측 Drawer 폼 open
 *   - 저장: 평면 DataSet 의 C/U/D 변경분을 한 번의 트랜잭션으로 전송
 *
 * 【Backend 호출】
 *   - POST /api/v1/transaction (service=menu/searchList) — 메뉴 전체 조회 (평면)
 *   - POST /api/v1/transaction (service=menu/save)       — 벌크 저장 (C/U/D)
 *
 * 【DataSet/Hook】
 *   - useDataSet<MenuRow>() — ds_menuList : 평면 메뉴 목록 (PK: menuId, FK: parentId)
 *   - useTransaction()      — RPC 통합 호출
 *   - useLabel()            — i18n 라벨(t)
 *
 * 【사용처】 router/index.tsx — `{ path: '/menu', element: <MenuManagementPage /> }`
 * 【포팅 원본】 vue-spring-fw `frontend/src/pages/PageMenuManagement.vue`
 * 【의존성】 antd Button/Card/Drawer/Form/Input/Switch/Tree/Tag, framework hooks
 */
import { useEffect, useMemo, useState } from 'react'
import { Button, Card, Drawer, Flex, Form, Input, Switch, Tree, Tag } from 'antd'
import type { DataNode } from 'antd/es/tree'
import CrudToolbar from '@/components/common/CrudToolbar'
import PageContainer from '@/components/common/PageContainer'
import { useDataSet, useTransaction } from '@/framework'
import { useLabel } from '@/framework/useLabel'

/** 메뉴 1행 — 평면 구조에서 parentId 로 트리 구성. */
interface MenuRow {
  /** 메뉴 PK (대문자 영숫자, 예: "MNU_USER"). */
  menuId: string
  /** 메뉴 표시명 (다국어 처리는 별도 fw_menu_i18n). */
  menuName: string
  /** 라우터 path (예: "/admin/user"). 그룹 노드는 비어있음. */
  menuPath?: string
  /** 상위 메뉴 ID (FK → MenuRow.menuId). 루트는 빈 문자열/undefined. */
  parentId?: string
  /** 동일 부모 내 정렬 순서 (오름차순). */
  sortOrder?: number
  /** antd Icon 식별자 (예: "UserOutlined"). */
  icon?: string
  /** 사용여부 ('Y'=노출 / 'N'=숨김). */
  useYn: 'Y' | 'N'
}

/**
 * 평면 rows → antd Tree 의 DataNode[] 트리 변환.
 * - 1) menuId 키 Map 으로 노드 생성
 * - 2) parentId 매칭 시 children push, 매칭 실패 시 root 후보
 * - title 에 _state 별 (신규/수정) Tag 시각화 포함
 */
function buildTree(
  rows: { data: MenuRow; _state: string; _rowId: string }[],
  t: (k: string, f?: string) => string
): DataNode[] {
  const map = new Map<string, DataNode & { data: MenuRow }>()
  rows.forEach((r) => {
    map.set(r.data.menuId, {
      key: r.data.menuId || r._rowId,
      title: (
        <span>
          {r.data.menuName || <i style={{ color: '#888' }}>(new)</i>}{' '}
          <span style={{ color: '#aaa', fontSize: 12 }}>{r.data.menuPath}</span>
          {r._state === 'C' && <Tag color="green">{t('TTL_ADD', '신규')}</Tag>}
          {r._state === 'U' && <Tag color="orange">{t('TTL_EDIT', '수정')}</Tag>}
        </span>
      ),
      children: [],
      data: r.data
    })
  })
  const roots: DataNode[] = []
  rows.forEach((r) => {
    const node = map.get(r.data.menuId)!
    const pid = r.data.parentId
    if (pid && map.has(pid)) map.get(pid)!.children!.push(node)
    else roots.push(node)
  })
  return roots
}

/**
 * MenuManagementPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function MenuManagementPage() {
  const ds = useDataSet<MenuRow>()
  const { transaction } = useTransaction()
  const { t } = useLabel()
  const [selectedId, setSelectedId] = useState<string>('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [form] = Form.useForm()

  /** 메뉴 전체 조회 — menu/searchList → ds (평면). 검색 조건 없음. */
  const search = () =>
    transaction({ id: 'select', service: 'menu/searchList', outds: { ds_menuList: ds } })

  useEffect(() => {
    search()
  }, [])

  const treeData = useMemo(
    () => buildTree(ds.getVisibleRows() as { data: MenuRow; _state: string; _rowId: string }[], t),
    [ds.rows, t]
  )

  const currentIdx = () => ds.getVisibleRows().findIndex((r) => r.data.menuId === selectedId)

  /** 루트 메뉴 추가 — parentId 비어있는 신규 행 push. */
  const addRoot = () => {
    ds.addRow({ menuId: '', menuName: '', parentId: '', sortOrder: 0, useYn: 'Y' } as MenuRow)
  }
  /** 하위 메뉴 추가 — 선택 노드를 부모로 신규 행 push. 미선택 시 무동작. */
  const addChild = () => {
    if (!selectedId) return
    ds.addRow({
      menuId: '',
      menuName: '',
      parentId: selectedId,
      sortOrder: 0,
      useYn: 'Y'
    } as MenuRow)
  }
  /** 선택 메뉴 삭제 — 신규(C)는 즉시 제거, 기존(U)은 _state='D' 마킹. */
  const del = () => {
    const idx = currentIdx()
    if (idx >= 0) ds.deleteRow(idx)
  }
  /** 선택 메뉴 편집 — 폼에 현재 값 세팅 후 Drawer open. */
  const edit = () => {
    const idx = currentIdx()
    if (idx < 0) return
    form.setFieldsValue(ds.getVisibleRows()[idx].data)
    setDrawerOpen(true)
  }
  /** Drawer 적용 — 폼 값을 DataSet 에 update (자동으로 _state='U' 마킹). */
  const onFormFinish = (values: MenuRow) => {
    const idx = currentIdx()
    if (idx >= 0) ds.updateRow(idx, values)
    setDrawerOpen(false)
  }
  /** 벌크 저장 — menu/save (C/U/D 변경분만 전송). 성공 시 재조회로 동기화. */
  const save = async () => {
    const res = await transaction({
      id: 'save',
      service: 'menu/save',
      action: 'save',
      inds: { ds_menuList: ds },
      outds: { ds_menuList: ds }
    })
    if (res.success) search()
  }

  return (
    <PageContainer>
      <Flex
        justify="space-between"
        align="center"
        gap={8}
        style={{ flexShrink: 0, flexWrap: 'nowrap', overflowX: 'auto' }}
      >
        <Button onClick={addChild} disabled={!selectedId} style={{ flexShrink: 0 }}>
          {t('BTN_ADD_CHILD_MENU', '하위 메뉴 추가')}
        </Button>
        <CrudToolbar
          onSearch={search}
          onAdd={addRoot}
          onEdit={edit}
          onDelete={del}
          onSave={save}
          dirty={ds.isDirty}
          style={{ width: 'auto', flexShrink: 0 }}
        />
      </Flex>
      <Card
        size="small"
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
        styles={{ body: { flex: 1, minHeight: 0, overflow: 'auto', padding: 8 } }}
      >
        <Tree
          showLine={{ showLeafIcon: false }}
          blockNode
          treeData={treeData}
          selectedKeys={selectedId ? [selectedId] : []}
          onSelect={(keys) => setSelectedId((keys[0] as string) ?? '')}
          defaultExpandAll
        />
      </Card>
      <Drawer
        title={t('TTL_MENU_EDIT', '메뉴 편집')}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={420}
        extra={
          <Button type="primary" onClick={() => form.submit()}>
            {t('BTN_APPLY', '적용')}
          </Button>
        }
      >
        <Form form={form} layout="vertical" onFinish={onFormFinish}>
          <Form.Item label={t('LBL_MENU_ID', '메뉴ID')} name="menuId" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            label={t('LBL_MENU_NAME', '메뉴명')}
            name="menuName"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label={t('LBL_PATH', '경로')} name="menuPath">
            <Input />
          </Form.Item>
          <Form.Item label={t('LBL_PARENT_ID', '상위ID')} name="parentId">
            <Input />
          </Form.Item>
          <Form.Item label={t('LBL_SORT_ORDER', '정렬순서')} name="sortOrder">
            <Input type="number" />
          </Form.Item>
          <Form.Item label={t('LBL_ICON', '아이콘')} name="icon">
            <Input />
          </Form.Item>
          <Form.Item
            label={t('LBL_USE_YN', '사용여부')}
            name="useYn"
            valuePropName="checked"
            getValueFromEvent={(v) => (v ? 'Y' : 'N')}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </PageContainer>
  )
}
